import { useMemo, useState } from "react";

function fmtUSD(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function fmtNum(n) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

const SLIDERS = [
  { id: "volume", label: "Monthly ticket volume", min: 100, max: 10000, step: 50, def: 2500, suffix: " tickets" },
  { id: "minutes", label: "Avg. minutes spent manually per ticket", min: 5, max: 60, step: 1, def: 18, suffix: " min" },
  { id: "locators", label: "Number of locators", min: 1, max: 500, step: 1, def: 24, suffix: "" },
  { id: "rate", label: "Admin hourly cost", min: 15, max: 75, step: 1, def: 32, prefix: "$", suffix: "/hr" },
];

export default function ROICalculator() {
  const [values, setValues] = useState({
    volume: 2500, minutes: 18, locators: 24, rate: 32,
  });

  const results = useMemo(() => {
    const hoursSaved = (values.volume * values.minutes / 60) * 0.4;
    const monthlySavings = hoursSaved * values.rate;
    return {
      hoursSaved: Math.round(hoursSaved),
      monthlySavings: Math.round(monthlySavings),
      annualSavings: Math.round(monthlySavings * 12),
    };
  }, [values]);

  const onChange = (id) => (e) => {
    const v = Number(e.target.value);
    setValues((s) => ({ ...s, [id]: v }));
  };

  return (
    <section data-testid="roi-section" className="section-y relative">
      <div className="container-x">
        <div className="max-w-3xl reveal mb-10">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#F97316] mb-3">ROI Calculator</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Estimate your operational impact.
          </h2>
          <p className="mt-4 text-base text-[#94A3B8]">
            Inputs are estimates. Results are directional — not guaranteed.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 reveal">
          {/* Inputs */}
          <div className="rounded-2xl glass p-7 space-y-6">
            {SLIDERS.map((s) => {
              const v = values[s.id];
              return (
                <div key={s.id}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-white font-medium">{s.label}</label>
                    <span className="font-mono text-sm text-[#F97316]">
                      {s.prefix || ""}{fmtNum(v)}{s.suffix || ""}
                    </span>
                  </div>
                  <input
                    type="range"
                    data-testid={`roi-slider-${s.id}`}
                    className="locate-slider"
                    min={s.min} max={s.max} step={s.step} value={v}
                    onChange={onChange(s.id)}
                  />
                  <div className="flex justify-between text-[10px] font-mono text-[#94A3B8] mt-1.5">
                    <span>{s.prefix || ""}{fmtNum(s.min)}{s.suffix || ""}</span>
                    <span>{s.prefix || ""}{fmtNum(s.max)}{s.suffix || ""}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Outputs */}
          <div className="space-y-4">
            <div className="rounded-2xl glass p-6">
              <div className="text-xs text-[#94A3B8] uppercase tracking-widest font-mono">Est. admin hours saved / month</div>
              <div data-testid="roi-hours-saved" className="font-mono font-extrabold text-4xl text-[#F97316] mt-2">
                {fmtNum(results.hoursSaved)}<span className="text-xl text-white/60 ml-2">hours</span>
              </div>
              <div className="text-xs text-[#94A3B8] mt-2">Assumes 40% reduction in manual admin time.</div>
            </div>
            <div className="rounded-2xl glass p-6">
              <div className="text-xs text-[#94A3B8] uppercase tracking-widest font-mono">Est. monthly cost savings</div>
              <div data-testid="roi-monthly-savings" className="font-mono font-extrabold text-4xl text-[#F97316] mt-2">
                {fmtUSD(results.monthlySavings)}
              </div>
              <div className="text-xs text-[#94A3B8] mt-2">
                Annualized: <span className="text-white">{fmtUSD(results.annualSavings)}</span>
              </div>
            </div>
            <div className="rounded-2xl glass p-6">
              <div className="text-xs text-[#94A3B8] uppercase tracking-widest font-mono">Locator productivity gain</div>
              <div className="text-white mt-2 leading-relaxed">
                Standardizing closeouts across{" "}
                <span className="text-[#F97316] font-bold font-mono">{fmtNum(values.locators)}</span>{" "}
                locators reduces rework and re-inspection time.
              </div>
            </div>
            <p className="text-[11px] text-[#94A3B8]/80 italic">
              These are rough estimates based on industry benchmarks. Actual results vary by operation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
